import { useCascadeStore } from '../store'
import { FUZZINESS_CONFIG, VALIDATION_RULESET_CONFIG, type ValidationRuleset, type FuzzinessLevel, type CascadeNode } from '../types'

export function ValidationDetailPanel({ node }: { node: CascadeNode }) {
  const { setValidationVendor, setValidationRuleset, toggleVerifiedField, setVerifiedFieldFuzziness, setNodeNotes } = useCascadeStore()

  // Secondary vendor nodes only need a name and notes
  if (node.isSecondaryVendor) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <span className="text-xl">🛡️</span>
          <div>
            <h3 className="text-xs font-bold text-[var(--text-primary)]">Additional Vendor</h3>
            <p className="text-[10px] text-[var(--text-muted)]">
              Ruleset & fields inherited from primary
            </p>
          </div>
        </div>

        <div>
          <span className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wider mb-1.5 block">
            Vendor
          </span>
          <input
            type="text"
            value={node.validationVendor ?? ''}
            onChange={(e) => setValidationVendor(node.id, e.target.value)}
            placeholder="e.g. Experian, Equifax, GDC..."
            className="w-full text-[11px] px-2.5 py-2 rounded-lg bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div>
          <span className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wider mb-1.5 block">
            Notes
          </span>
          <textarea
            value={node.nodeNotes ?? ''}
            onChange={(e) => setNodeNotes(node.id, e.target.value)}
            placeholder="e.g. Used as fallback if primary returns partial match..."
            className="w-full text-[11px] p-2.5 rounded-lg bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none leading-relaxed"
            rows={3}
          />
        </div>
      </div>
    )
  }

  const enabledCount = node.verifiedFields?.filter(f => f.enabled).length ?? 0

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <span className="text-xl">🛡️</span>
        <div>
          <h3 className="text-xs font-bold text-[var(--text-primary)]">
            {node.label}
          </h3>
          <p className="text-[10px] text-[var(--text-muted)]">
            Configure vendor, ruleset & verified fields
          </p>
        </div>
      </div>

      {/* Vendor */}
      <div>
        <span className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wider mb-1.5 block">
          Vendor
        </span>
        <input
          type="text"
          value={node.validationVendor ?? ''}
          onChange={(e) => setValidationVendor(node.id, e.target.value)}
          placeholder="e.g. DVS, Experian, Equifax, GDC..."
          className="w-full text-[11px] px-2.5 py-2 rounded-lg bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>

      {/* Ruleset selector */}
      <div>
        <span className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wider mb-1.5 block">
          Ruleset
        </span>
        <div className="flex flex-wrap gap-1">
          {(Object.entries(VALIDATION_RULESET_CONFIG) as [ValidationRuleset, { label: string; description: string }][]).map(([id, config]) => (
            <button
              key={id}
              onClick={() => setValidationRuleset(node.id, id)}
              className={`text-[10px] px-2.5 py-1 rounded-md transition-colors ${
                node.validationRuleset === id
                  ? 'bg-blue-600 text-white'
                  : 'bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-primary)]'
              }`}
              title={config.description}
            >
              {config.label}
            </button>
          ))}
        </div>
        {node.validationRuleset && (
          <p className="text-[9px] text-[var(--text-muted)] mt-1">
            {VALIDATION_RULESET_CONFIG[node.validationRuleset as ValidationRuleset]?.description || node.validationRuleset}
          </p>
        )}
      </div>

      {/* Verified fields */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wider">
            Fields Verified
          </span>
          <span className="text-[10px] text-[var(--text-muted)]">
            {enabledCount} selected
          </span>
        </div>

        <div className="space-y-1">
          {node.verifiedFields?.map(field => (
            <div
              key={field.id}
              className={`rounded-lg border transition-all ${
                field.enabled
                  ? 'border-blue-500/30 bg-blue-500/5'
                  : 'border-transparent bg-[var(--bg-tertiary)] opacity-50 hover:opacity-75'
              }`}
            >
              <div className="flex items-center gap-2 px-2.5 py-1.5">
                {/* Toggle switch */}
                <button
                  onClick={() => toggleVerifiedField(node.id, field.id)}
                  className={`shrink-0 w-7 h-4 rounded-full transition-colors relative ${
                    field.enabled ? 'bg-blue-500' : 'bg-gray-600'
                  }`}
                >
                  <div
                    className="absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform"
                    style={{ left: 2, transform: field.enabled ? 'translateX(12px)' : 'translateX(0)' }}
                  />
                </button>

                {/* Label */}
                <span className={`text-[11px] font-medium flex-1 ${
                  field.enabled ? 'text-[var(--text-primary)]' : 'text-[var(--text-muted)]'
                }`}>
                  {field.label}
                </span>

                {/* Fuzziness dropdown */}
                {field.enabled && (
                  <select
                    value={field.fuzziness}
                    onChange={(e) => setVerifiedFieldFuzziness(node.id, field.id, e.target.value as FuzzinessLevel)}
                    className="text-[9px] px-1 py-0.5 rounded bg-transparent border-none text-[var(--text-muted)] cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    {(Object.entries(FUZZINESS_CONFIG) as [FuzzinessLevel, typeof FUZZINESS_CONFIG.strict][]).map(([level, config]) => (
                      <option key={level} value={level}>{config.label}</option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Notes */}
      <div>
        <span className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wider mb-1.5 block">
          Notes
        </span>
        <textarea
          value={node.nodeNotes ?? ''}
          onChange={(e) => setNodeNotes(node.id, e.target.value)}
          placeholder="e.g. Fallback if primary bureau returns partial match..."
          className="w-full text-[11px] p-2.5 rounded-lg bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none leading-relaxed"
          rows={3}
        />
      </div>
    </div>
  )
}
